import { ReOrderRegular } from "@fluentui/react-icons";
import { AppSpinner } from "components/AppSpinner/AppSpinner";
import { ActionButton } from "components/styled";
import { useBenefitCardsOrderListConfig } from "hooks/useBenefitCategoryOrderListIndexes";
import { PrimitiveAtom, useAtom } from "jotai";
import { APP_ID, APP_SETTING_ID } from "library/constants";
import {
  EAppSettings,
  IBenefitCategory,
  IBenefitImplementation,
  IBenefitPackage,
} from "library/types";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAddAppSetting } from "state/queries/useAddAppSetting";
import { useUpdateAppSettings } from "state/queries/useUpdateAppSettings";

type Props = {
  itemList: IBenefitCategory[] | IBenefitImplementation[] | IBenefitPackage[];
  disabled: boolean;
  settingKey: string;
  itemIdProperty: string;
  orderedAtom: PrimitiveAtom<any[]>;
  searchFilterValue?: string;
};

export function OrderSubmitContent({
  itemList,
  disabled,
  orderedAtom,
  itemIdProperty,
  settingKey,
  searchFilterValue,
}: Props) {
  const { t } = useTranslation();

  const [orderedItem, setOrderedItem] = useAtom(orderedAtom);

  const { mutate: onAddAppSetting, isLoading: addSettingsInProgress } =
    useAddAppSetting();
  const { mutate: onUpdateAppSetting, isLoading: updateSettingsInProgress } =
    useUpdateAppSettings();

  const { allCardsOrderConfig, orderedList, benefitCardsOrderListAppSettings } =
    useBenefitCardsOrderListConfig({
      benefitKey: settingKey,
      itemIdProperty,
      rawList: itemList || [],
      defaultOrderedList: (itemList || []).map((item, index) => {
        return {
          ...item,
          tag: item[itemIdProperty]?.toString(),
          id: (index + 1).toString(),
        };
      }),
    });

  useEffect(() => {
    setOrderedItem(
      orderedList?.filter((item) =>
        item?.name
          ?.toLowerCase()
          .includes(searchFilterValue ? searchFilterValue.toLowerCase() : "")
      )
    );
  }, [itemList, searchFilterValue]); // Update orderedItem when itemList or searchFilterValue changes

  return (
    <>
      {addSettingsInProgress || updateSettingsInProgress ? (
        <AppSpinner />
      ) : null}
      <ActionButton
        icon={<ReOrderRegular />}
        appearance="subtle"
        disabled={disabled}
        noBorder
        onClick={() => {
          if (benefitCardsOrderListAppSettings) {
            onUpdateAppSetting(
              {
                payload: [
                  {
                    ...benefitCardsOrderListAppSettings,
                    userAppSettingValue: JSON.stringify({
                      ...allCardsOrderConfig,
                      [settingKey]: orderedItem.map((item) => {
                        return {
                          tag: item[itemIdProperty],
                          id: item.id,
                        };
                      }),
                    }),
                  },
                ] as any,
              },
              {
                onSuccess: () => {
                  toast.success(t("hbh.orderSaved.label"));
                },
              }
            );
          } else {
            onAddAppSetting(
              {
                payload: [
                  {
                    applicationCodeId: APP_ID,
                    userAppSettingValue: JSON.stringify({
                      [settingKey]: orderedItem.map((item) => {
                        return {
                          tag: item[itemIdProperty],
                          id: item.id,
                        };
                      }),
                    }),
                    appSettingCodeId: APP_SETTING_ID,
                    userAppSettingName:
                      EAppSettings.benefitCardsOrderListConfig,
                    isDefault: false,
                  },
                ],
              },
              {
                onSuccess: () => {
                  toast.success(t("hbh.orderSaved.label"));
                },
              }
            );
          }
        }}
      >
        {t("hbh.saveOrder.label")}
      </ActionButton>
    </>
  );
}
